import { useCallback, useRef, useState } from 'react';

export type ContainerRef<T extends HTMLElement> = (node: T | null) => void;

/**
 * Measures the element so the timeline can lay out in real pixels. A callback ref,
 * not an effect: the node only mounts once loading clears, which an effect would miss.
 */
export function useContainerWidth<T extends HTMLElement>(): [ContainerRef<T>, number] {
    const [width, setWidth] = useState(0);
    const observer = useRef<ResizeObserver | null>(null);

    const ref = useCallback((node: T | null) => {
        observer.current?.disconnect();
        observer.current = null;
        if (!node) return;

        setWidth(node.clientWidth);
        if (typeof ResizeObserver === 'undefined') return;
        observer.current = new ResizeObserver((entries) => setWidth(entries[0].contentRect.width));
        observer.current.observe(node);
    }, []);

    return [ref, width];
}
